// Черновик формы CompanyProfile.vue — чистые функции без Vue и $store.
//
// Форма редактирует локальную копию компании, а стор меняется только мутацией с
// новым объектом (Vuex docs, «Form Handling»). Новый объект в сторе появляется не
// только от своих сохранений: рассылка CompanyChannel после правки коллеги,
// refreshCompanyData(), ответы соседних сохранений, клиентская копия
// RentToOwnSettingsCard. Черновик догоняет его трёхсторонним слиянием:
//
//   base  — значения сервера, от которых черновик отсчитывает правки;
//   draft — то, что видит и правит пользователь;
//   next  — новый объект стора.
//
// Поле без правки (draft == base) берёт значение из next; поле с правкой остаётся
// набранным — при конфликте побеждает пользователь осознанным «Сохранить»: молча
// заменить то, что человек видит у себя под курсором, хуже. Новая база — next,
// поэтому конфликт остаётся несохранённой правкой, а откат к серверному — нет.
//
// Ключа нет в next — поле и база не трогаются: рассылка (BroadcastCompanyJob,
// as_json) несёт не все ключи ответа GET (docx_templates, periodic_payment_templates),
// и отсутствие значит «сервер не прислал», а не «удалено».
//
// Списки строк с id (кассы, вебхуки, курсы) сливаются построчно: правленая строка
// остаётся своей (той же ссылкой — поле под курсором не перерисовывается), остальные
// и новые строки коллеги берутся с сервера, строки без id (добавленные, но не
// сохранённые) дописываются в конец. Строку удалили на сервере — она уходит из формы
// и с правкой: сохранить её некуда (PATCH ушёл бы на несуществующий id). Списки
// значений (counts_groups) — целиком.

// Сравнение по значению, без учёта порядка ключей: GET отдаёт to_json, рассылка —
// as_json, и порядок ключей у одной и той же строки не обязан совпадать.
function stable(value) {
  if (Array.isArray(value)) return value.map(stable);
  if (value && typeof value === "object") {
    return Object.keys(value)
      .sort()
      .reduce((acc, key) => {
        acc[key] = stable(value[key]);
        return acc;
      }, {});
  }
  return value;
}

export function sameValue(a, b) {
  return JSON.stringify(stable(a)) === JSON.stringify(stable(b));
}

// Глубокая копия JSON-данных API: черновик и база не должны делить ни одного
// вложенного объекта со стором — иначе v-model на строке кассы снова писал бы в стор.
function copy(value) {
  return value === undefined ? undefined : JSON.parse(JSON.stringify(value));
}

export function cloneCompany(company) {
  return company ? copy(company) : null;
}

const hasOwn = (obj, key) => obj != null && Object.prototype.hasOwnProperty.call(obj, key);

// Глубокая копия перечисленных полей, которые есть в объекте: что именно ушло в
// запрос сохранения (sent для rebaseDraft, тело PATCH панелей интеграций).
export function pickValues(obj, keys) {
  const result = {};
  keys.forEach((key) => {
    if (hasOwn(obj, key)) result[key] = copy(obj[key]);
  });
  return result;
}

const isRow = (value) => value !== null && typeof value === "object" && !Array.isArray(value);

function isRowList(...lists) {
  if (!lists.every(Array.isArray)) return false;
  const rows = [].concat(...lists);
  return rows.every(isRow) && rows.some((row) => row.id != null);
}

function byId(rows) {
  const map = new Map();
  rows.forEach((row) => {
    if (row.id != null) map.set(row.id, row);
  });
  return map;
}

function mergeRows(draftRows, baseRows, nextRows) {
  const drafts = byId(draftRows);
  const bases = byId(baseRows);
  const merged = nextRows.map((row) => {
    const draft = drafts.get(row.id);
    const base = bases.get(row.id);
    if (draft && base && !sameValue(draft, base)) return draft;
    if (draft && sameValue(draft, row)) return draft;
    return copy(row);
  });
  draftRows.filter((row) => row.id == null).forEach((row) => merged.push(row));
  return merged;
}

const plainSet = (obj, key, value) => {
  obj[key] = value;
};

// Подтягивает в draft (на месте) значения next по правилу выше и возвращает новую
// базу. sent — значения полей, ушедших в своём PATCH: поле, не менявшееся с
// отправки, берёт ответ сервера, даже если тот его нормализовал ("22" → 22); поле,
// набранное за время запроса, остаётся правкой. Поля вне payload в sent нет — их
// несохранённые правки ответ не трогает.
// set — Vue.set: ключ может впервые появиться в черновике и обязан быть реактивным.
export function rebaseDraft(draft, base, next, { sent = null, set = plainSet } = {}) {
  Object.keys(next).forEach((key) => {
    const edited = !sameValue(draft[key], base[key]);
    const savedAsSent = !!sent && hasOwn(sent, key) && sameValue(draft[key], sent[key]);
    let value;
    if (!edited || savedAsSent) {
      value = copy(next[key]);
    } else if (isRowList(draft[key], base[key], next[key])) {
      value = mergeRows(draft[key], base[key], next[key]);
    } else {
      return;
    }
    if (!sameValue(draft[key], value)) set(draft, key, value);
  });
  return { ...base, ...copy(next) };
}
